
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
    pending_counselling,
    pending_counselling_counts,
    update_status,
    driver_default_pharmacist,
    save_pharmacist_for_driver,
    pharmacy_has_linked_otp,
    check_unassigned_vital_patients
} from "../../../networking/urlEndPoints";

import Axios from "../../../networking/interceptor";

// Thunk: Fetch patients data
export const fetchPendingCounsellingPatientsData = createAsyncThunk(
    'pendingCounselling/fetchPatients',
    async ({ pharmacyId }, { rejectWithValue }) => {
        try {
            const { data } = await Axios.get(pending_counselling, {
                params: {
                    pharmacy_id: pharmacyId,
                    per_page: 50
                }
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Thunk: Fetch counts data
export const fetchPendingCounsellingCounts = createAsyncThunk(
    'pendingCounselling/fetchCounts',
    async ({ pharmacyId }, { rejectWithValue }) => {
        try {
            const { data } = await Axios.get(pending_counselling_counts, {
                params: { pharmacy_id: pharmacyId }
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Thunk: Update status
export const updateCounsellingStatus = createAsyncThunk(
    'pendingCounselling/updateStatus',
    async ({ pharmacyId,
        newRxCounsellingValues,
        dispatch }, { rejectWithValue }) => {

        const payloadData = {
            pharmacy_id: pharmacyId,
        }

        if (newRxCounsellingValues?.length > 0) {
            const formattedCounsellingNotes = {};
            newRxCounsellingValues.forEach(({ rxid, dropdownValue }) => {
                if (dropdownValue?.value) {
                    formattedCounsellingNotes[rxid] = dropdownValue.value;
                }
                payloadData.pending_counseling_note = formattedCounsellingNotes;
            });
        }

        try {
            const { data } = await Axios.post(update_status, payloadData);
            dispatch(fetchPendingCounsellingPatientsData({
                pharmacyId: pharmacyId
            }))
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Thunk: Fetch all pharmacists list
export const fetchAllPharmacistList = createAsyncThunk(
    'pendingCounselling/fetchAllPharmacistList',
    async ({ pharmacyId }, { rejectWithValue }) => {
        try {
            const { data } = await Axios.get(driver_default_pharmacist, {
                params: { pharmacy_id: pharmacyId }
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Thunk: Save pharmacist for driver
export const savePharmacistForDriver = createAsyncThunk(
    'pendingCounselling/savePharmacistForDriver',
    async ({ reqData }, { rejectWithValue }) => {
        try {
            const { pharmacy_id, pharmacist_id } = reqData
            const payloadData = {
                pharmacy_id,
                pharmacist_id
            }
            const { data } = await Axios.post(save_pharmacist_for_driver, payloadData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Thunk: Check pharmacy linked OTP status
export const fetchPharmacyHasLinkedOtp = createAsyncThunk(
    'pendingCounselling/fetchPharmacyHasLinkedOtp',
    async ({ pharmacyId }, { rejectWithValue }) => {
        try {
            const { data } = await Axios.get(pharmacy_has_linked_otp, {
                params: { pharmacy_id: pharmacyId }
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Thunk: Check unassigned vital patients
export const fetchCheckUnassignedVitalPatients = createAsyncThunk(
    'pendingCounselling/fetchCheckUnassignedVitalPatients',
    async ({ pharmacyId }, { rejectWithValue }) => {
        try {
            const { data } = await Axios.get(check_unassigned_vital_patients, {
                params: { pharmacy_id: pharmacyId }
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Action: Store flexi marked notification
export const storeFlexiMarkedNotification = createAsyncThunk(
    'pendingCounselling/storeFlexiMarkedNotification',
    async (notificationData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const existingNotifications = state.pendingCounselling.flexiMarkedNotifications.data || [];
            
            // Check if patient_id already exists
            const existingIndex = existingNotifications.findIndex(
                notification => notification.rxnumber === notificationData.rxnumber
            );
            
            let updatedNotifications;
            if (existingIndex !== -1) {
                // Update existing notification
                updatedNotifications = [...existingNotifications];
                updatedNotifications[existingIndex] = notificationData;
            } else {
                // Add new notification
                updatedNotifications = [...existingNotifications, notificationData];
            }
            return updatedNotifications;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Initial State
const initialState = {
    patients: { loading: false, data: null, error: null },
    counts: { loading: false, data: null, error: null },
    updateStatus: { loading: false, data: null, error: null },
    allPharmacist: { loading: false, data: null, error: null },
    savePharmacistForDriver: { loading: false, data: null, error: null },
    pharmacyHasLinkedOtp: { loading: false, data: null, error: null },
    checkUnassignedVitalPatients: { loading: false, data: null, error: null },
    flexiMarkedNotifications: { loading: false, data: [], error: null },
};

// Helper to add reducers for a thunk
const handleAsyncThunk = (builder, thunk, key) => {
    builder
        .addCase(thunk.pending, (state) => {
            state[key].loading = true;
            state[key].error = null;
            if (key !== 'flexiMarkedNotifications') {
                state[key].data = null;
            }
        })
        .addCase(thunk.fulfilled, (state, action) => {
            state[key].loading = false;
            state[key].data = action.payload;
            state[key].error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
            state[key].loading = false;
            state[key].error = action.payload;
            state[key].data = null;
        });
};

// Slice
const pendingCounsellingSlice = createSlice({
    name: 'pendingCounselling',
    initialState,
    reducers: {
        // Clear flexi marked notifications
        clearFlexiMarkedNotifications: (state) => {
            state.flexiMarkedNotifications.data = [];
            state.flexiMarkedNotifications.error = null;
        },
        // Remove specific flexi marked notification by rxnumber
        removeFlexiMarkedNotification: (state, action) => {
            const rxnumber = action.payload;
            state.flexiMarkedNotifications.data = state.flexiMarkedNotifications.data.filter(
                notification => notification.rxnumber !== rxnumber
            );
        }
    },
    extraReducers: (builder) => {
        handleAsyncThunk(builder, fetchPendingCounsellingPatientsData, 'patients');
        handleAsyncThunk(builder, fetchPendingCounsellingCounts, 'counts');
        handleAsyncThunk(builder, updateCounsellingStatus, 'updateStatus');
        handleAsyncThunk(builder, fetchAllPharmacistList, 'allPharmacist');
        handleAsyncThunk(builder, savePharmacistForDriver, 'savePharmacistForDriver');
        handleAsyncThunk(builder, fetchPharmacyHasLinkedOtp, 'pharmacyHasLinkedOtp');
        handleAsyncThunk(builder, fetchCheckUnassignedVitalPatients, 'checkUnassignedVitalPatients');
        handleAsyncThunk(builder, storeFlexiMarkedNotification, 'flexiMarkedNotifications');
    }
});

export const { clearFlexiMarkedNotifications, removeFlexiMarkedNotification } = pendingCounsellingSlice.actions;

export default pendingCounsellingSlice.reducer;
